import { css, Global } from '@emotion/react';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useEffect, useLayoutEffect } from 'react';
import { clearHsChatOpenHash, HS_CHAT_OPEN_HASH, isHsChatOpenHash, useHsChatOpen } from './HubspotHideChatStyles';
import { ensureHubspotChatScript } from './useHubspotChatLoadStatus';
export { clearHsChatOpenHash, HS_CHAT_OPEN_HASH, HubspotHideChatStyles, isHsChatOpenHash, useHsChatOpen } from './HubspotHideChatStyles';
export { useHubspotChatLoadStatus } from './useHubspotChatLoadStatus';
type HubspotConversationSDK = {
  on?: (event: string, handler: (payload?: unknown) => void) => void;
  off?: (event: string, handler: (payload?: unknown) => void) => void;
  widget: {
    load: (args?: {
      widgetOpen?: boolean;
    }) => void;
    open: () => void;
    close: () => void;
    status?: () => {
      loaded?: boolean;
    };
  };
};
type HubspotWindow = Window & {
  HubSpotConversations?: HubspotConversationSDK;
  hsConversationsOnReady?: Array<() => void> | (() => void);
  hsConversationsSettings?: {
    loadImmediately: boolean;
    enableWidgetCookieBanner: boolean;
    disableAttachment: boolean;
    disableInitialInputFocus: boolean;
  };
};
const HS_CHAT_UNHIDE_ATTR = 'data-hs-chat-open-unhide';
const OPEN_RETRY_MS = 250;
const OPEN_RETRY_TIMEOUT_MS = 15000;
const hubspotUnhideCssText = 'html body div#hubspot-messages-iframe-container{display:block!important;visibility:visible!important;pointer-events:auto!important;}';
const getHubspotWindow = (): HubspotWindow | null => typeof window === 'undefined' ? null : window as HubspotWindow;
const injectNativeUnhideStyle = () => {
  if (typeof document === 'undefined') {
    return;
  }
  if (document.head.querySelector(`style[${HS_CHAT_UNHIDE_ATTR}]`)) {
    return;
  }
  const style = document.createElement('style');
  style.setAttribute(HS_CHAT_UNHIDE_ATTR, '');
  style.textContent = hubspotUnhideCssText;
  document.head.appendChild(style);
};
const removeNativeUnhideStyle = () => {
  if (typeof document === 'undefined') {
    return;
  }
  document.head.querySelector(`style[${HS_CHAT_UNHIDE_ATTR}]`)?.remove();
};
const hubspotWindow = getHubspotWindow();
if (hubspotWindow) {
  hubspotWindow.hsConversationsSettings = {
    loadImmediately: false,
    enableWidgetCookieBanner: false,
    disableAttachment: false,
    disableInitialInputFocus: false
  };
  if (isHsChatOpenHash(hubspotWindow.location.hash)) {
    injectNativeUnhideStyle();
  }
}
const hubspotUnhideStyles = css`
  html body div#hubspot-messages-iframe-container {
    display: block !important;
    visibility: visible !important;
    pointer-events: auto !important;
  }
`;
const removeHubspotHideGlobalStyles = () => {
  if (typeof document === 'undefined') {
    return;
  }
  document.querySelectorAll('style[data-emotion*="css-global"]').forEach(node => {
    const text = node.textContent || '';
    if (text.includes('hubspot-messages-iframe-container') && /display\s*:\s*none/i.test(text)) {
      node.remove();
    }
  });
};
const isHubspotChatOpen = () => typeof document !== 'undefined' && document.documentElement.classList.contains('hs-messages-widget-open');
const tryOpenHubspotWidget = (win: HubspotWindow) => {
  const widget = win.HubSpotConversations?.widget;
  if (!widget) {
    return false;
  }
  try {
    const loaded = widget.status?.()?.loaded;
    if (loaded === false) {
      widget.load({
        widgetOpen: true
      });
    }
    widget.open();
    return true;
  } catch {
    try {
      widget.open();
      return true;
    } catch {
      return false;
    }
  }
};
const prepareChatOpen = () => {
  injectNativeUnhideStyle();
  removeHubspotHideGlobalStyles();
};
const hrefHash = (href: string) => {
  try {
    return new URL(href, window.location.href).hash;
  } catch {
    return '';
  }
};

/** Open HubSpot chat via the Conversations SDK. Sets `#hs-chat-open` so unhide CSS stays in sync. */
export const openHubspotChat = () => {
  const win = getHubspotWindow();
  if (!win) {
    return;
  }
  prepareChatOpen();
  tryOpenHubspotWidget(win);
  if (!isHsChatOpenHash()) {
    win.location.hash = HS_CHAT_OPEN_HASH;
  }
};
export const HubspotChatHandler = () => {
  const {
    currentUser
  } = useCurrentUser();
  const hsChatOpen = useHsChatOpen();
  useEffect(() => {
    ensureHubspotChatScript();
  }, []);
  useLayoutEffect(() => {
    if (!isHsChatOpenHash()) {
      removeNativeUnhideStyle();
      return;
    }
    prepareChatOpen();
  }, [hsChatOpen]);
  useEffect(() => {
    const onHsChatOpenLinkClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      const anchor = target.closest('a');
      if (!anchor?.href || !isHsChatOpenHash(hrefHash(anchor.href))) {
        return;
      }
      if (!isHsChatOpenHash()) {
        return;
      }
      event.preventDefault();
      openHubspotChat();
    };
    document.addEventListener('click', onHsChatOpenLinkClick, true);
    return () => document.removeEventListener('click', onHsChatOpenLinkClick, true);
  }, []);
  useEffect(() => {
    const win = getHubspotWindow();
    if (!win) {
      return;
    }
    let cancelled = false;
    let intervalId: number | undefined;
    let timeoutId: number | undefined;
    let observer: MutationObserver | undefined;
    let onWidgetLoaded: (() => void) | undefined;
    let onWidgetClosed: (() => void) | undefined;
    const stopRetries = () => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };
    const openChat = () => {
      if (cancelled || !isHsChatOpenHash()) {
        return isHubspotChatOpen();
      }
      prepareChatOpen();
      tryOpenHubspotWidget(win);
      return isHubspotChatOpen();
    };
    const closeChatIfRegistered = () => {
      if (isHsChatOpenHash() || !currentUser?.isRegistered) {
        return;
      }
      try {
        win.HubSpotConversations?.widget?.close();
      } catch {
        // Widget may not be loaded yet.
      }
    };
    const startOpenRetries = () => {
      if (cancelled || !isHsChatOpenHash()) {
        return;
      }
      if (openChat()) {
        return;
      }
      stopRetries();
      intervalId = window.setInterval(() => {
        if (cancelled || openChat()) {
          stopRetries();
        }
      }, OPEN_RETRY_MS);
      timeoutId = window.setTimeout(stopRetries, OPEN_RETRY_TIMEOUT_MS);
    };
    const onReady = () => {
      if (cancelled) {
        return;
      }
      onWidgetClosed = () => {
        if (!cancelled) {
          clearHsChatOpenHash();
        }
      };
      win.HubSpotConversations?.on?.('widgetClosed', onWidgetClosed);
      if (isHsChatOpenHash()) {
        onWidgetLoaded = () => {
          if (!cancelled && isHsChatOpenHash()) {
            openChat();
          }
        };
        win.HubSpotConversations?.on?.('widgetLoaded', onWidgetLoaded);
        startOpenRetries();
      } else {
        closeChatIfRegistered();
      }
    };
    if (isHsChatOpenHash()) {
      observer = new MutationObserver(removeHubspotHideGlobalStyles);
      observer.observe(document.head, {
        childList: true
      });
      startOpenRetries();
    } else {
      closeChatIfRegistered();
    }
    if (win.HubSpotConversations) {
      onReady();
    } else {
      const existing = win.hsConversationsOnReady;
      win.hsConversationsOnReady = [...(Array.isArray(existing) ? existing : existing ? [existing] : []), onReady];
    }
    return () => {
      cancelled = true;
      stopRetries();
      observer?.disconnect();
      if (onWidgetLoaded) {
        win.HubSpotConversations?.off?.('widgetLoaded', onWidgetLoaded);
      }
      if (onWidgetClosed) {
        win.HubSpotConversations?.off?.('widgetClosed', onWidgetClosed);
      }
      if (Array.isArray(win.hsConversationsOnReady)) {
        win.hsConversationsOnReady = win.hsConversationsOnReady.filter(fn => fn !== onReady);
      }
    };
  }, [currentUser?.isRegistered, hsChatOpen]);
  return hsChatOpen ? <Global styles={hubspotUnhideStyles} /> : <></>;
};